<template>
    <div>
        <h3 class="my-3 text-lg font-medium leading-6 text-gray-400">
            Reservas de Saldo por Produtor
        </h3>

        <form
            @submit.prevent="fetch(true)"
            class="flex content-center items-center"
        >
            <div class="grow">
                <input
                    v-model="filters.text"
                    type="text"
                    class="text-input"
                    placeholder="Pesquise pelo produtor: nome, e-mail ou ID"
                />
            </div>
            <div class="ml-2 flex flex-none items-center space-x-2">
                <button
                    @click.prevent="clearFilters"
                    :disabled="loading"
                    type="button"
                    :class="{ 'cursor-not-allowed': loading }"
                    class="btn-outline-primary px-4 py-1"
                >
                    Limpar
                </button>
                <submit-button
                    :loading="loading"
                    @click.prevent="fetch(true)"
                    size="sm"
                >
                    Pesquisar
                </submit-button>
                <button
                    @click.prevent="exportCsv"
                    :disabled="exporting || loading || !canExport"
                    type="button"
                    class="btn-primary-sm"
                >
                    <ArrowDownTrayIcon
                        class="mr-1.5 h-5 w-5 shrink-0 text-white"
                        aria-hidden="true"
                    />
                    {{ exporting ? 'Exportando...' : 'Exportar CSV' }}
                </button>
            </div>
        </form>

        <div class="mt-4 overflow-auto rounded-xl bg-slate-50 dark:bg-slate-800/25" style="max-height: calc(100vh - 280px)">
            <table class="min-w-full table-auto border-collapse text-sm">
                <thead class="sticky top-0 z-10 bg-slate-50 dark:bg-slate-700">
                    <tr class="text-slate-700 dark:text-white">
                        <th class="px-6 py-4 text-left text-sm font-medium">Produtor</th>
                        <th class="px-6 py-4 text-left text-sm font-medium">E-mail</th>
                        <th class="px-6 py-4 text-right text-sm font-medium">Valor Reservado</th>
                        <th class="px-6 py-4 text-center text-sm font-medium">Dias de Retenção</th>
                        <th class="px-6 py-4 text-center text-sm font-medium">Data da Reserva</th>
                        <th class="px-6 py-4 text-center text-sm font-medium">Data de Liberação</th>
                        <th class="px-6 py-4 text-center text-sm font-medium">Status</th>
                    </tr>
                </thead>
                <tbody class="bg-white dark:bg-slate-800">
                    <tr v-if="!searched">
                        <td colspan="7" class="p-8 text-center text-slate-400">
                            Pesquise um produtor para visualizar as reservas.
                        </td>
                    </tr>
                    <tr v-else-if="loading">
                        <td colspan="7" class="p-8 text-center text-slate-400">
                            Carregando...
                        </td>
                    </tr>
                    <tr v-else-if="rows.length === 0">
                        <td colspan="7" class="p-8 text-center text-slate-400">
                            Nenhuma reserva encontrada para este produtor.
                        </td>
                    </tr>
                    <tr v-for="row in rows" :key="row.id">
                        <td class="border-b border-slate-100 p-4 pl-8 text-slate-500 dark:border-slate-700 dark:text-slate-200">
                            {{ row.producer }}
                        </td>
                        <td class="border-b border-slate-100 p-4 pl-8 text-slate-500 dark:border-slate-700 dark:text-slate-200">
                            {{ row.email }}
                        </td>
                        <td class="border-b border-slate-100 p-4 text-right text-slate-500 dark:border-slate-700 dark:text-slate-200">
                            {{ formatCurrency(row.reserved_amount) }}
                        </td>
                        <td class="border-b border-slate-100 p-4 text-center text-slate-500 dark:border-slate-700 dark:text-slate-200">
                            {{ row.cushion_days }}
                        </td>
                        <td class="border-b border-slate-100 p-4 text-center text-slate-500 dark:border-slate-700 dark:text-slate-200">
                            {{ row.reserved_at }}
                        </td>
                        <td class="border-b border-slate-100 p-4 text-center font-medium text-slate-600 dark:border-slate-700 dark:text-slate-100">
                            {{ row.released_at }}
                        </td>
                        <td class="border-b border-slate-100 p-4 text-center dark:border-slate-700">
                            <span
                                class="rounded-full px-2.5 py-0.5 text-xs font-medium"
                                :class="row.status === 'Liberado'
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'"
                            >
                                {{ row.status }}
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div class="my-5 flex w-full" v-if="pagination.total > 0">
                <div class="mx-auto flex flex-col items-center">
                    <span class="mb-1 text-white">
                        Exibindo {{ pagination.from }} a
                        {{ pagination.to }} registros de
                        {{ pagination.total }}
                    </span>
                    <pagination
                        :pagination="pagination"
                        :offset="1"
                        @paginate="fetchPage"
                    ></pagination>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ArrowDownTrayIcon } from "@heroicons/vue/24/outline";
import Pagination from "../../../components/layouts/Pagination.vue";
import SubmitButton from "../../../components/forms/SubmitButton.vue";

export default {
    components: { Pagination, ArrowDownTrayIcon, SubmitButton },
    data() {
        return {
            loading: false,
            exporting: false,
            searched: false,
            filters: {
                text: '',
            },
            rows: [],
            pagination: {
                total: 0,
                per_page: 25,
                from: 0,
                to: 0,
                current_page: 1,
                last_page: 1,
            },
        };
    },
    computed: {
        canExport() {
            return this.searched && this.filters.text.trim().length >= 2;
        },
    },
    methods: {
        fetch(reset = false) {
            if (this.filters.text.trim().length < 2) {
                return;
            }

            if (reset) {
                this.pagination.current_page = 1;
            }

            this.loading = true;
            this.searched = true;
            const params = {
                text: this.filters.text,
                page: this.pagination.current_page,
                per_page: this.pagination.per_page,
            };
            axios
                .get('/api/financial/balance-reserve-report', { params })
                .then(({ data }) => {
                    const rows = Array.isArray(data.data) ? data.data : [];
                    const total = Number(data.total ?? rows.length) || 0;
                    const perPage = Number(data.per_page ?? this.pagination.per_page) || this.pagination.per_page;
                    const currentPage = Number(data.current_page ?? this.pagination.current_page) || this.pagination.current_page;
                    const lastPage = Number(data.last_page ?? Math.max(Math.ceil(total / perPage), 1)) || 1;
                    const from = Number(data.from ?? (total > 0 ? (currentPage - 1) * perPage + 1 : 0));
                    const to = Number(data.to ?? Math.min(currentPage * perPage, total));

                    this.rows = rows;
                    this.pagination = {
                        ...this.pagination,
                        total,
                        from,
                        to,
                        current_page: currentPage,
                        per_page: perPage,
                        last_page: lastPage,
                    };
                })
                .catch((error) => {
                    this.rows = [];
                    this.pagination = {
                        ...this.pagination,
                        total: 0,
                        from: 0,
                        to: 0,
                        current_page: 1,
                        last_page: 1,
                    };

                    if (this.$toast) {
                        this.$toast.error(error?.response?.data?.message || 'Erro ao carregar reservas de saldo.');
                    }

                    console.error('Erro ao buscar reservas de saldo:', error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        fetchPage(page) {
            this.pagination.current_page = page;
            this.fetch();
        },
        clearFilters() {
            this.filters.text = '';
            this.searched = false;
            this.rows = [];
            this.pagination = {
                ...this.pagination,
                total: 0,
                from: 0,
                to: 0,
                current_page: 1,
                last_page: 1,
            };
        },
        exportCsv() {
            if (!this.canExport) {
                return;
            }

            this.exporting = true;
            const params = {
                text: this.filters.text,
            };
            axios
                .get('/api/financial/balance-reserve-report/export', { params })
                .then(({ data }) => {
                    const csvContent = data.rows
                        .map((row) => row.map((cell) => `"${cell}"`).join(';'))
                        .join('\n');
                    const blob = new Blob(
                        ['﻿' + csvContent],
                        { type: 'text/csv;charset=utf-8;' }
                    );
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = data.filename;
                    link.click();
                    URL.revokeObjectURL(url);
                    this.exporting = false;
                })
                .catch((error) => {
                    if (this.$toast) {
                        this.$toast.error(error?.response?.data?.message || 'Erro ao exportar reservas de saldo.');
                    }

                    console.error('Erro ao exportar reservas de saldo:', error);
                    this.exporting = false;
                });
        },
        formatCurrency(value) {
            return new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
            }).format(value);
        },
    },
};
</script>
